// Shared helpers for the Kit delivery page (`/kit/:id`) and its sub-components.
// Extracted from Kit.jsx so DayCard / ReelsStrip / future kit modules can be
// imported without dragging the entire 1600-line monolith.
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Resolve a possibly-relative API URL to an absolute one. */
export const absUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

/** Same-origin download proxy — forces Content-Disposition: attachment and
 *  makes the bytes fetchable for the Web Share API regardless of R2 CORS. */
const proxyUrl = (url, filename) =>
    `${BACKEND}/api/media/dl?u=${encodeURIComponent(url)}&name=${encodeURIComponent(filename)}`;

/** Save an image or video with the friendliest path per device:
 *  - iOS / Android: fetch bytes → Web Share sheet → "Save Image / Save Video"
 *    puts it straight into the phone gallery (ready to post to IG).
 *  - If the share sheet isn't available: navigate to the proxy URL, whose
 *    attachment header forces a real download (no more long-press dance).
 *  - Desktop: one click → downloads via the proxy attachment header. */
export async function saveImage(url, filename) {
    const proxy = proxyUrl(url, filename);
    const isVideo = /\.(mp4|mov|webm)(\?|$)/i.test(filename) || /\.(mp4|mov|webm)(\?|$)/i.test(url);

    if (isMobile()) {
        if (navigator.share && navigator.canShare) {
            const t = toast.loading(isVideo ? "Preparing video…" : "Preparing image…");
            try {
                const res = await fetch(proxy);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                const type = blob.type && blob.type !== "application/octet-stream"
                    ? blob.type : (isVideo ? "video/mp4" : "image/png");
                const file = new File([blob], filename, { type });
                if (navigator.canShare({ files: [file] })) {
                    toast.dismiss(t);
                    await navigator.share({ files: [file] });
                    toast.success(isVideo ? "Tap “Save Video” to add it to your gallery 🎬" : "Tap “Save Image” to add it to your gallery 📸", { duration: 5000 });
                    return;
                }
                toast.dismiss(t);
            } catch (err) {
                toast.dismiss(t);
                if (err?.name === "AbortError") return; // user closed the share sheet
                console.warn("[saveImage] share flow failed:", err);
            }
        }
        // Fallback: forced download via attachment header (Android → Downloads,
        // iOS → Files). No page navigation happens for attachment responses.
        window.location.href = proxy;
        toast.success("Downloading…", {
            description: "Android: check your gallery/Downloads. iPhone: check Files ↓ then save to Photos.",
            duration: 6000,
        });
        return;
    }

    // Desktop — one click, straight to the Downloads folder.
    try {
        const link = document.createElement("a");
        link.href = proxy;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Downloading", { description: filename });
    } catch (err) {
        console.warn("[saveImage] proxy download failed:", err);
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success("Opened in new tab", { description: "Right-click → Save as…", duration: 6000 });
    }
}

/** Save MANY images with ONE action — no more accepting them one by one.
 *  - iOS / Android with Web Share: fetch all bytes → ONE share sheet →
 *    "Save N Images" puts the whole batch straight into the phone gallery.
 *  - Share unavailable/failed: fall back to a single ZIP download (zipUrl).
 *  - Desktop: single ZIP download. */
export async function saveImages(items, zipUrl) {
    if (isMobile() && navigator.share && navigator.canShare) {
        const t = toast.loading(`Preparing ${items.length} images…`);
        try {
            const files = [];
            for (const it of items) {
                const res = await fetch(proxyUrl(it.url, it.filename));
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                const type = blob.type && blob.type !== "application/octet-stream" ? blob.type : "image/png";
                files.push(new File([blob], it.filename, { type }));
            }
            if (navigator.canShare({ files })) {
                toast.dismiss(t);
                await navigator.share({ files });
                toast.success(`Tap "Save ${items.length > 1 ? `${items.length} Images` : "Image"}" to add all to your gallery 📸`, { duration: 6000 });
                return;
            }
            toast.dismiss(t);
        } catch (err) {
            toast.dismiss(t);
            if (err?.name === "AbortError") return; // user closed the share sheet
            console.warn("[saveImages] batch share failed:", err);
        }
        if (zipUrl) {
            window.location.href = zipUrl;
            toast.success("Downloading all images as ZIP…", {
                description: "Android: check Downloads. iPhone: open in Files → tap to extract → save to Photos.",
                duration: 7000,
            });
            return;
        }
        for (const it of items) await saveImage(it.url, it.filename);
        return;
    }
    // Desktop — one ZIP, one click.
    if (zipUrl) {
        const link = document.createElement("a");
        link.href = zipUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Downloading ${items.length} images as ZIP`);
        return;
    }
    for (const it of items) await saveImage(it.url, it.filename);
}

/** True on iOS / Android UA, where `instagram://` deep links actually fire. */
export function isMobile() {
    if (typeof navigator === "undefined") return false;
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/** Open the Instagram app's create flow (Camera/Create screen on mobile,
 *  instagram.com tab on desktop). IG does NOT accept a pre-filled caption via
 *  URL params — caller MUST copy the caption to clipboard first. */
export function openInstagramCreate() {
    if (isMobile()) {
        window.location.href = "instagram://camera";
        // Fallback: if the scheme silently fails (unlikely on modern iOS/Android),
        // open IG web in a new tab.
        setTimeout(() => {
            window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
        }, 1200);
    } else {
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    }
}
